import { useEffect, useState } from "react";
import { useServiceFaq } from "../../hooks/service/useServiceFaq";
import { FaqItem, FaqCreateRequest, FaqUpdateRequest } from "../../types/faq";
import { DataTableColumn } from "../../types/dataTable";
import DataTable from "../../components/common/DataTable";
import CommonSidebar from "../../components/dashboard/CommonSidebar";
import NoticeModal from "../../components/service/NoticeModal";
import toast from "react-hot-toast";

export default function Faq() {
  const {
    faqList,
    totalCount,
    currentPage,
    totalPages,
    isLoading,
    error,
    isEmpty,
    fetchFaqList,
    createFaq,
    updateFaq,
    setCurrentPage,
  } = useServiceFaq();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FaqItem | null>(null);
  const [isModalLoading, setIsModalLoading] = useState(false);

  useEffect(() => {
    fetchFaqList(1);
  }, [fetchFaqList]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const columns: DataTableColumn<FaqItem>[] = [
    {
      key: "title",
      header: "제목",
      render: (value, row) => (
        <div className="max-w-2xl">
          <h3 className="text-white font-medium truncate">{value}</h3>
          <p className="text-slate-400 text-sm truncate mt-1">
            {row.description}
          </p>
        </div>
      ),
    },
    {
      key: "notification_type",
      header: "타입",
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
          {value === "faq" ? "FAQ" : value}
        </span>
      ),
    },
    {
      key: "created_at",
      header: "생성일",
      render: (value) => (
        <div className="text-slate-300 text-sm">{formatDate(value)}</div>
      ),
    },
    {
      key: "reference_image",
      header: "이미지",
      render: (value) => (
        <div className="text-slate-400 text-sm">{value ? "있음" : "없음"}</div>
      ),
    },
    {
      key: "actions",
      header: "관리",
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleEditClick(row)}
            className="px-3 py-1 text-xs bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors"
          >
            수정
          </button>
        </div>
      ),
    },
  ];

  const paginationInfo = {
    currentPage,
    totalPages,
    totalCount,
    startIndex: (currentPage - 1) * 10,
    endIndex: Math.min(currentPage * 10, totalCount),
    onPageChange: (page: number) => {
      setCurrentPage(page);
      fetchFaqList(page);
    },
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingFaq(null);
  };

  const handleFaqSubmit = async (data: FaqCreateRequest) => {
    setIsModalLoading(true);
    try {
      if (editingFaq) {
        await updateFaq(editingFaq.id, data as FaqUpdateRequest);
        toast.success("FAQ가 수정되었습니다.");
      } else {
        await createFaq(data);
        toast.success("FAQ가 생성되었습니다.");
      }
    } catch (error) {
      console.error("FAQ 제출 중 오류 발생:", error);
      toast.error("FAQ 제출 중 오류가 발생했습니다.");
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleEditClick = (faq: FaqItem) => {
    setEditingFaq(faq);
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">FAQ 관리</h1>
        </div>
        <CommonSidebar />
        <div className="p-6">
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 mb-6 flex flex-col gap-4">
            <div className="flex gap-2">
              <button
                onClick={() => handleEditClick(null as unknown as FaqItem)}
                disabled={isLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {isLoading ? "처리중..." : "FAQ 생성"}
              </button>
            </div>
          </div>
          <div
            className={`bg-slate-900 rounded-xl border border-slate-800 overflow-hidden p-6`}
          >
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
              <span className="ml-3 text-slate-400">데이터를 불러오는 중...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">FAQ 관리</h1>
        </div>
        <CommonSidebar />
        <div className="p-6">
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 mb-6 flex flex-col gap-4">
            <div className="flex gap-2">
              <button
                onClick={() => handleEditClick(null as unknown as FaqItem)}
                disabled={isLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {isLoading ? "처리중..." : "FAQ 생성"}
              </button>
            </div>

            <div className="text-slate-400 text-sm">
              총 {totalCount}개의 FAQ | 현재 페이지: {currentPage}/{totalPages}
            </div>
          </div>

          <DataTable<FaqItem>
            columns={columns}
            data={faqList}
            pagination={paginationInfo}
            loading={false}
            error={null}
            emptyMessage="작성된 FAQ가 없습니다."
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">FAQ 관리</h1>
        </div>
        <CommonSidebar />
        <div className="p-6">
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 mb-6 flex flex-col gap-4">
            <div className="flex gap-2">
              <button
                onClick={() => handleEditClick(null as unknown as FaqItem)}
                disabled={isLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {isLoading ? "처리중..." : "FAQ 생성"}
              </button>
            </div>

            <div className="text-slate-400 text-sm">
              총 {totalCount}개의 FAQ | 현재 페이지: {currentPage}/{totalPages}
            </div>
          </div>

          <DataTable<FaqItem>
            columns={columns}
            data={[]}
            pagination={undefined}
            loading={false}
            error={error}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 lg:ml-72">
      <div className="bg-slate-900 border-b border-slate-800 p-6">
        <h1 className="text-2xl font-bold text-white">FAQ 관리</h1>
      </div>
      <CommonSidebar />
      <div className="p-6">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 mb-6 flex flex-col gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => handleEditClick(null as unknown as FaqItem)}
              disabled={isLoading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "처리중..." : "FAQ 생성"}
            </button>
          </div>

          <div className="text-slate-400 text-sm">
            총 {totalCount}개의 FAQ | 현재 페이지: {currentPage}/{totalPages}
          </div>
        </div>

        <DataTable<FaqItem>
          columns={columns}
          data={faqList}
          pagination={paginationInfo}
          loading={isLoading}
          error={error}
        />
      </div>

      <NoticeModal<FaqItem, FaqCreateRequest>
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={async (data: FaqCreateRequest) => {
          await handleFaqSubmit(data);
          return true;
        }}
        isLoading={isModalLoading}
        editingNotice={editingFaq}
        modalTitle="FAQ"
        createButtonText="생성"
        editButtonText="수정"
      />
    </div>
  );
}
